/* eslint-disable react-hooks/exhaustive-deps */
import { FC, memo, useContext, useEffect, useState } from 'react';
import { Circle, GoogleMap, Marker, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api';
import AppContext from 'src/store/appContext';
import { configuration } from 'src/configuration';
import AutoComp from '../Autocomplete';

import styles from './GoogleMap.module.scss';
import { INearbyMapSurvey } from 'src/interfaces/interfaces';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import GpsInput from '../GpsInput/index';

const hlmpLocation = {
  lat: 50.08712109608858,
  lng: 14.417847885347497,
};

const createClusterIcon = (fillColor: string, strokeColor: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="20" fill="${fillColor}" fill-opacity="0.92" stroke="${strokeColor}" stroke-width="4"/>
    </svg>
  `)}`;

const clusterStyles = [
  {
    url: createClusterIcon('#2dcfa7', '#ffffff'),
    height: 48,
    width: 48,
    textColor: '#111111',
    textSize: 14,
    fontWeight: '700',
  },
  {
    url: createClusterIcon('#2878d7', '#ffffff'),
    height: 48,
    width: 48,
    textColor: '#ffffff',
    textSize: 14,
    fontWeight: '700',
  },
  {
    url: createClusterIcon('#f0a202', '#ffffff'),
    height: 48,
    width: 48,
    textColor: '#111111',
    textSize: 14,
    fontWeight: '700',
  },
  {
    url: createClusterIcon('#d7263d', '#ffffff'),
    height: 48,
    width: 48,
    textColor: '#ffffff',
    textSize: 14,
    fontWeight: '700',
  },
  {
    url: createClusterIcon('#8b1e3f', '#ffffff'),
    height: 48,
    width: 48,
    textColor: '#ffffff',
    textSize: 14,
    fontWeight: '700',
  },
];

const GMap: FC<{
  centerPosition?: google.maps.LatLngLiteral;
  isAutocomplete?: boolean;
  mapHeight?: string;
  nearests?: INearbyMapSurvey[];
  radius?: number;
}> = ({ centerPosition, isAutocomplete, mapHeight, nearests, radius }) => {
  const { LatLng, setLatLng, detail } = useContext(AppContext);
  const zoom = 18;
  const [center, setCenter] = useState<google.maps.LatLngLiteral>(hlmpLocation);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: configuration.GOOGLE_API_KEY || '',
    libraries: configuration.GOOGLE_LIBS,
    language: 'cs',
    region: 'cz',
  });

  const [showInfo, setShowInfo] = useState(false);
  const [showSameLocation, setShowSameLocation] = useState(false);
  const [sames, setSames] = useState<INearbyMapSurvey[]>();

  const [isMarkerMoveMode, setIsMarkerMoveMode] = useState(false);

  const [isFocused, setIsFocused] =
    useState<{ focused: boolean; latLng: google.maps.LatLngLiteral | undefined }>();

  const markerPosition = centerPosition || LatLng;
  const hasExternalCenter = !!centerPosition;

  const onClick = (e: google.maps.MapMouseEvent) => {
    if (!LatLng) {
      setLatLng(() => e.latLng?.toJSON());
    }
    setShowInfo(false);
    setShowSameLocation(false);
    setIsFocused({ focused: false, latLng: undefined });

    if (isMarkerMoveMode && LatLng) {
      setLatLng(() => e.latLng?.toJSON());
      setCenterHandler(e.latLng?.toJSON()!);
      setShowInfo(false);
      setIsFocused({ focused: false, latLng: undefined });
      // Additional logic or actions you may want to perform after moving the marker
    }
  };

  const resetPositionHandler = () => {
    setLatLng({
      lat: Number(detail?.gps?.split(',')[0]),
      lng: Number(detail?.gps?.split(',')[1]),
    });
  };

  const setCenterHandler = (LatLng: google.maps.LatLngLiteral) => {
    setCenter({ lat: LatLng.lat, lng: LatLng.lng });
  };

  const handleMarkerLoad = (marker: google.maps.Marker) => {
    setCenterHandler(marker.getPosition()!.toJSON());
  };

  const getMarkerposition = (e: google.maps.MapMouseEvent) => {
    setLatLng(() => e.latLng?.toJSON());
    setCenterHandler(e.latLng!.toJSON());
  };

  const mapContainerStyle = {
    width: '100%',
    height: '100%',
    border: '3px solid #2dcfa7',
  };

  const ResetPosition = () => {
    return (
      <div
        style={{
          padding: '0.5rem 0',
        }}
      >
        <button
          className={`btn ${styles['reset-button']}`}
          type="button"
          onClick={resetPositionHandler}
          aria-label="Resetovat polohu"
        >
          Resetovat polohu
        </button>
      </div>
    );
  };

  const SameLocations = () => {
    return (
      <div className={`${styles['info-window']} ${styles['info-window-same-locs']}`}>
        <span>Podnět(y) na poloze:</span>
        <ul>
          {sames?.map((place) => (
            <li key={place.reg_num}>
              <a href={`/detail/${place.id}`} target="_blank" rel="noreferrer">
                {place.reg_num}
              </a>
              <p>{place.description}</p>
              <p className="mb-0 small text-muted">
                {place.status_name}, vzdálenost {Math.round(place.distance)} m
              </p>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const InfoWin = () => {
    return (
      <div className={styles['info-window']}>
        <span>Info o podnětu:</span>
        <ul>
          <li>RegNum: {detail?.reg_num}</li>
          <li>Lat: {markerPosition?.lat}</li>
          <li>Lng: {markerPosition?.lng}</li>
        </ul>
      </div>
    );
  };

  const showInfoHandler = (e: google.maps.MapMouseEvent) => {
    setCenterHandler(e.latLng?.toJSON()!);
    setShowInfo(true);
    setShowSameLocation(false);
    setIsFocused({ focused: false, latLng: undefined });
  };

  const showSameLocationHandler = (lat: number, lng: number) => {
    const sameArr = nearests?.filter((pos) => pos.lat === lat && pos.lng === lng);
    setSames(sameArr);
    setShowSameLocation(true);
  };

  useEffect(() => {
    if (!hasExternalCenter && LatLng) {
      setCenterHandler(LatLng);
    }
  }, [hasExternalCenter, LatLng]);

  useEffect(() => {
    if (centerPosition) {
      setCenterHandler(centerPosition);
    }
  }, [centerPosition]);

  useEffect(() => {
    if (!hasExternalCenter && detail?.gps) {
      resetPositionHandler();
    }
  }, [detail?.gps, hasExternalCenter]);

  return (
    <div
      style={{
        display: 'flex',
        flexFlow: 'column',
        height: mapHeight || (isAutocomplete ? '600px' : '450px'),
        minHeight: '300px',
      }}
    >
      {!isLoaded && <span>Nahrávám...</span>}
      {isLoaded && (
        <>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            id={'google-map'}
            zoom={zoom}
            center={center}
            onClick={onClick}
          >
            {markerPosition && nearests && radius && (
              <Circle
                center={markerPosition}
                radius={radius}
                options={{
                  clickable: false,
                  fillColor: '#2dcfa7',
                  fillOpacity: 0.06,
                  strokeColor: '#2dcfa7',
                  strokeOpacity: 0.55,
                  strokeWeight: 2,
                  zIndex: 1,
                }}
              />
            )}
            {LatLng && !nearests && (
              <Marker
                position={LatLng}
                onDragEnd={getMarkerposition}
                onLoad={handleMarkerLoad}
                draggable={!nearests}
                onClick={() => setIsMarkerMoveMode(!isMarkerMoveMode)}
                icon={{
                  // Change the marker color based on the selected state
                  path: faLocationDot.icon[4] as string,
                  fillColor: '#f00',
                  fillOpacity: 0.9,
                  anchor: new google.maps.Point(
                    faLocationDot.icon[0] / 2, // horizontal shift - centered
                    500 // vertical shift
                  ),
                  strokeWeight: isMarkerMoveMode ? 2 : 1,
                  strokeColor: isMarkerMoveMode ? '#00f' : '#f00',
                  scale: 0.085,
                  rotation: 180,
                }}
              />
            )}
            {markerPosition && nearests && (
              <Marker
                position={markerPosition}
                title={`Poloha podnětu ${detail?.reg_num}`}
                icon={{
                  path: faLocationDot.icon[4] as string,
                  fillColor: '#f00',
                  fillOpacity: 0.9,
                  anchor: new google.maps.Point(
                    faLocationDot.icon[0] / 2, // horizontal shift - centered
                    450 // vertical shift
                  ),
                  strokeWeight: 1,
                  strokeColor: '#fff',
                  scale: 0.085,
                  rotation: 180,
                }}
                animation={google.maps.Animation.DROP}
                onClick={showInfoHandler}
              />
            )}
            {showInfo && <InfoWin />}
            {sames && sames?.length > 0 && showSameLocation && <SameLocations />}
            {/* Show individual markers above zoom 18, including coincident locations. */}
            {nearests && (
              <MarkerClusterer styles={clusterStyles} gridSize={40} maxZoom={18}>
                {(clusterer) => (
                  <>
                    {nearests.map((loc) => (
                      <Marker
                        key={loc.id}
                        clusterer={clusterer}
                        position={{ lat: loc.lat, lng: loc.lng }}
                        title={loc.reg_num}
                        icon={{
                          path: faLocationDot.icon[4] as string,
                          fillColor:
                            isFocused?.focused &&
                            isFocused.latLng?.lat === loc.lat &&
                            isFocused.latLng.lng === loc.lng
                              ? '#f00'
                              : '#00f',
                          fillOpacity: 1,
                          anchor: new google.maps.Point(
                            faLocationDot.icon[0] / 2, // horizontal shift - centered
                            550 // vertical shift - positioned on top of location
                          ),
                          strokeWeight: 1,
                          strokeColor: '#fff',
                          scale: 0.075,
                        }}
                        onClick={(e: google.maps.MapMouseEvent) => {
                          setCenterHandler(e.latLng?.toJSON()!);
                          setShowInfo(false);
                          setIsFocused({ focused: true, latLng: e.latLng?.toJSON()! });
                          showSameLocationHandler(loc.lat, loc.lng);
                        }}
                      />
                    ))}
                  </>
                )}
              </MarkerClusterer>
            )}
          </GoogleMap>
          {!nearests && (
            <>
              <ResetPosition />
              <AutoComp />
              <GpsInput />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default memo(GMap);
